import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Panel from '@/features/report/shared/Panel/Panel';
import type { CanonicalMeasurement, MeasurementGroup } from '@/models/template';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import panelStyles from '@/features/report/Measurements/MeasurementsPanel.module.css';
import { DragPreviewRow } from './DragPreviewRow';
import { EditableMeasurementGroup } from './EditableMeasurementGroup';
import { GROUP_DROP_PREFIX } from './measurementsDnd';

interface EditableMeasurementsSectionProps {
  groups: MeasurementGroup[];
  title?: string;
  onChange: (groups: MeasurementGroup[]) => void;
}

export function EditableMeasurementsSection({
  groups,
  title = 'Measurements',
  onChange,
}: EditableMeasurementsSectionProps) {
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.index - b.index),
    [groups]
  );
  const { data: canonicalMeasurements = [] } =
    useGetWorksheetCanonicalMeasurementsQuery();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const activeLabel = useMemo(() => {
    if (!activeId) return null;
    return (
      sortedGroups
        .flatMap((g) => g.measurementLabels)
        .find((l) => l.uuid === activeId) ?? null
    );
  }, [activeId, sortedGroups]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  // Build a key → canonical lookup for fast resolution in row renders
  const canonicalMeasurementsByKey = useMemo(() => {
    const map = new Map<string, CanonicalMeasurement>();
    canonicalMeasurements.forEach((c) => map.set(c.key, c));
    return map;
  }, [canonicalMeasurements]);

  // Keys already used elsewhere in this section so we can hide them from
  // the picker (each canonical measurement may only appear once per worksheet).
  const usedKeys = useMemo(() => {
    const used = new Set<string>();
    sortedGroups.forEach((g) =>
      g.measurementLabels.forEach((ml) => {
        if (ml.canonicalMeasurementKey) used.add(ml.canonicalMeasurementKey);
      })
    );
    return used;
  }, [sortedGroups]);

  const handleGroupChange = useCallback(
    (uuid: string, next: MeasurementGroup) => {
      onChange(sortedGroups.map((g) => (g.uuid === uuid ? next : g)));
    },
    [sortedGroups, onChange]
  );

  /* ───────────────────────────────────────────────────────────────────── */
  /*  Cross-group drag handler.                                           */
  /*  active.id is the dragged label uuid.                                */
  /*  over.id may be a label uuid OR a group container id (group:<uuid>). */
  /* ───────────────────────────────────────────────────────────────────── */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeUuid = String(active.id);
      const overId = String(over.id);

      const sourceGroup = sortedGroups.find((g) =>
        g.measurementLabels.some((l) => l.uuid === activeUuid)
      );
      if (!sourceGroup) return;

      let destGroup = sortedGroups.find((g) =>
        g.measurementLabels.some((l) => l.uuid === overId)
      );
      if (!destGroup && overId.startsWith(GROUP_DROP_PREFIX)) {
        const groupUuid = overId.slice(GROUP_DROP_PREFIX.length);
        destGroup = sortedGroups.find((g) => g.uuid === groupUuid);
      }
      if (!destGroup) return;

      const movedItem = sourceGroup.measurementLabels.find(
        (l) => l.uuid === activeUuid
      );
      if (!movedItem) return;

      if (sourceGroup.uuid === destGroup.uuid) {
        const oldIdx = sourceGroup.measurementLabels.findIndex(
          (l) => l.uuid === activeUuid
        );
        const newIdx = sourceGroup.measurementLabels.findIndex(
          (l) => l.uuid === overId
        );
        if (oldIdx === -1 || newIdx === -1) return;
        const reordered = arrayMove(
          sourceGroup.measurementLabels,
          oldIdx,
          newIdx
        ).map((l, i) => ({ ...l, index: i }));
        onChange(
          sortedGroups.map((g) =>
            g.uuid === sourceGroup.uuid
              ? { ...g, measurementLabels: reordered }
              : g
          )
        );
        return;
      }

      const newSourceLabels = sourceGroup.measurementLabels
        .filter((l) => l.uuid !== activeUuid)
        .map((l, i) => ({ ...l, index: i }));

      let insertAt: number;
      if (overId.startsWith(GROUP_DROP_PREFIX)) {
        insertAt = destGroup.measurementLabels.length;
      } else {
        insertAt = destGroup.measurementLabels.findIndex(
          (l) => l.uuid === overId
        );
        if (insertAt === -1) insertAt = destGroup.measurementLabels.length;
      }
      const newDestLabels = [
        ...destGroup.measurementLabels.slice(0, insertAt),
        movedItem,
        ...destGroup.measurementLabels.slice(insertAt),
      ].map((l, i) => ({ ...l, index: i }));

      onChange(
        sortedGroups.map((g) => {
          if (g.uuid === sourceGroup.uuid) {
            return { ...g, measurementLabels: newSourceLabels };
          }
          if (g.uuid === destGroup.uuid) {
            return { ...g, measurementLabels: newDestLabels };
          }
          return g;
        })
      );
    },
    [sortedGroups, onChange]
  );

  return (
    <Panel title={title}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className={panelStyles.grid}>
          {sortedGroups.map((group, idx) => (
            <EditableMeasurementGroup
              key={group.uuid}
              group={group}
              position={idx + 1}
              canonicalMeasurementsByKey={canonicalMeasurementsByKey}
              canonicalMeasurements={canonicalMeasurements}
              usedKeys={usedKeys}
              onChange={(next) => handleGroupChange(group.uuid, next)}
            />
          ))}
        </div>
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {activeLabel ? (
              <DragPreviewRow
                measurementLabel={activeLabel}
                canonicalMeasurementsByKey={canonicalMeasurementsByKey}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </Panel>
  );
}
